import { AddressFieldsType } from './AddressTypes';
import { AemConfigurationThirdpartySocialLoginProvider } from './AemConfigurationTypes';
import { CustomerAddress } from './checkout';
import { AuthStatusCode, EmailValidationResponseStatus } from './enums';
import { SupportedLanguages } from './GeneralTypes';

export type CustomerExistsResponse = {
  accountStatusCode: AuthStatusCode;
  accountStatusMessage?: string;
  hasIdentity: boolean;
  knownForFascia: boolean;
};

export type CustomerTitle = {
  id: number;
  title: string;
  sortOrder: number;
  idGender?: string;
};

export type CustomerGender = {
  id?: number;
  peopleCode: string;
  gender: string;
};

export type CustomerCreateRequestData = {
  email: string;
  firstName: string;
  gender: string;
  idLanguage?: number;
  idTitle: number;
  title?: string;
  language: string;
  lastName: string;
  middleName?: string;
  password: string;
  dateOfBirth?: string;
  mobilePhoneNumber?: string;
  preference: {
    preference: boolean;
    exploreMoreNewsletter: boolean;
  };
  finance?: {
    needInvoice: boolean;
    vatNumber: string;
    techAddress?: string; // @TODO remove optional when checkout part is done
    companyName: string;
    department: string;
    isVat?: boolean;
    isViesVerified?: boolean;
  };
  address?: AddressFieldsType;
};

export type CustomerPreferences = {
  advice_newsletter: boolean;
  allowed_to_phone: boolean;
  catalog_requested: boolean;
  email_newsletter: boolean;
  events_newsletter: boolean;
  mail_basket: boolean;
  mail_review: boolean;
  partner_newsletter: boolean;
  physical_newsletter: boolean;
  promotion_newsletter: boolean;
};

export type CustomerLanguage = {
  idLanguage: number;
  languageCode: string;
  languageDescriptionTranslations: Record<string, string>;
};
type CustomerLink = {
  href: string;
  rel: string;
  templated: boolean;
};
type CustomerCard = {
  links: Array<CustomerLink>;
  number: string;
};

type CustomerDeliveryAddress = CustomerAddressProfile & {
  companyName: string;
  defaultAddress: boolean;
  deliveryAddressId: number;
  isActive: boolean;
  mainAddress: boolean;
};

type CustomerAddressProfile = CustomerAddress & {
  links?: Array<CustomerLink>;
  shopId?: number;
};
type CustomerFinance = {
  accountManager: string;
  companyName: string;
  techAddress: string | null;
  department: string;
  exportToFinal: boolean;
  finalExportChanged: boolean;
  isVat: boolean;
  isViesVerified: boolean;
  jobTitle: string;
  links: Array<CustomerLink>;
  needInvoice: boolean;
  onAccount: boolean;
  onAccountBlocked: boolean;
  orderReferenceRequired: boolean;
  vatNumber: string;
};

export type CustomerProfile = {
  address: CustomerAddressProfile;
  card: Array<CustomerCard>;
  customerCode: string;
  customerIdentityId: string;
  dateOfBirth: string;
  deleteDateOfBirth: boolean;
  deliveryAddresses: Array<CustomerDeliveryAddress>;
  email: string;
  emailOnly: boolean;
  employee: boolean;
  eticket: boolean;
  finance: CustomerFinance;
  firstName: string;
  gender: string;
  idCustomer: number;
  idLanguage: number;
  idMarket: number;
  idTitle: number;
  isExploreMore: boolean;
  language: string;
  lastName: string;
  links: Array<CustomerLink>;
  middleName: string;
  mobilePhoneNumber: string;
  phoneNumber: string;
  primaryCustomerCard: string;
  title: string;
};

export interface CustomerAuthority {
  authority: string;
}

export interface CustomerBasicProfile {
  shopId: number;
  market: string;
  firstName: string;
  lastName: string;
  deprecatedUser: boolean;
  uuid: string;
  amcUuid: string;
  username: string;
  authorities: Array<CustomerAuthority>;
  enabled: boolean;
}

export type CustomerCountry = {
  canSelect?: boolean;
  countryCode: string;
  countryCodeLong: string;
  countryDescriptionTranslations: Record<SupportedLanguages, string>;
  idCountry: number;
  idZone: number;
  inWeb: boolean;
  isoCode: string;
  market?: string;
  showOnWeb?: boolean;
};

export type CustomerExploreMoreCard = {
  customerUUID: string | null;
  email: string | null;
  existingCustomer: boolean;
  validEmail: boolean;
};

export type CustomerVoucher = {
  amount: number;
  barcode: string;
  currency: string;
  currencyCode: string;
  description: string;
  maxUseable: number;
  message: string;
  minSpend: number;
  status: string;
  type: string;
  usage: string | null;
  valid: boolean;
  validFrom: string | null;
  validTo: string | null;
};

export type EmailValidationResponse = {
  status: EmailValidationResponseStatus;
  suggestedEmail: string | null;
};

export type SocialMediaConnectionsResponse = Array<SocialMediaConnection>;

export type SocialMediaConnection = {
  customerIdentityId: string;
  email: string;
  name: string;
  imageLink: string;
  externalId: string;
  type: string;
  customerSocialMediaLoginId: number;
  dateInserted: number;
  businessUnitId: number;
  marketId: number;
};

type SocialMediaPlatformError = {
  alreadyConnected: boolean;
  connectedToAnother: boolean;
};

export type SocialMediaPlatform = {
  title: AemConfigurationThirdpartySocialLoginProvider;
  connections: Array<SocialMediaConnection>;
  errors: SocialMediaPlatformError;
  type?: string;
};

export type TealiumCustomerData = {
  uuid: string;
  fbem: string;
  gem: string;
  ph: string;
  fn: string;
  ln: string;
  db: string;
  ge: string;
  ct: string;
  zp: string;
  country: string;
};

export enum TealiumCustomerIdentity {
  IDENTIFIED = 'identified',
  ANONYMOUS = 'anonymous',
}

export type ShoppingBudgetVoucher = {
  barcode: string;
  valid: boolean;
  message: string;
  messageCode: string;
};

export type ShoppingBudgetActivate = {
  success: boolean;
  vouchers: Array<ShoppingBudgetVoucher>;
};

export type Voucher = {
  barcode: string;
  valid: boolean;
  message: string;
  messageCode: string;
};

export type AppliedVoucher = {
  barcode: string;
  valid: boolean;
  amountOriginal: number;
  amountUnused: number;
  amountUsedForThisBasket: number;
  validFrom: number;
  validTo: number;
  message: string;
  singleUse: boolean;
  printable: null;
  showValidate: null;
  displayCode: string;
  type: string;
  minAmount: null;
  showAmount: boolean;
  description: null;
};

export type DiscountVoucherCreateData = {
  discountVoucherId: string;
  activationId: string;
  code: string;
  barcode: string;
  amount: number;
  runId: string;
  message: string;
  customerRequired: boolean;
  identificationRequired: boolean;
  showPopUp: boolean;
  valid: boolean;
};

export type CustomerLoyaltyVoucherData = {
  numberOfVouchers: number;
};
